/**
 * EnhancedBibleReader - Modern Bible reading experience
 * Features: Verse highlighting, notes, bookmarks, font controls, search
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { cn } from '@/lib/utils';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

// Icons
import {
  ChevronLeft, ChevronRight, Search, BookOpen, Menu,
  Highlighter, PenLine, Bookmark, BookmarkCheck, Share2,
  Settings, Type, Sun, Moon, Check, X, MoreVertical,
  Copy, List, Minus, Plus, CheckCircle, Globe
} from 'lucide-react';

// ============== Book Navigation Sidebar ==============
const BookNavigationSheet = ({ currentBook, currentChapter, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const { data: books = [] } = useQuery({
    queryKey: ['bible-books'],
    queryFn: () => api.get('/bible/books').then(r => r.data),
  });

  const filteredBooks = books.filter(book =>
    book.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const otBooks = filteredBooks.filter(b => b.testament === 'OT');
  const ntBooks = filteredBooks.filter(b => b.testament === 'NT');

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <List className="h-4 w-4" />
          <span className="hidden sm:inline">{currentBook} {currentChapter}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80">
        <SheetHeader>
          <SheetTitle>Select Book & Chapter</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <Input
            placeholder="Search books..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <ScrollArea className="h-[calc(100vh-200px)]">
            {/* Old Testament */}
            <div className="mb-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Old Testament
              </h3>
              <div className="space-y-1">
                {otBooks.map(book => (
                  <BookItem 
                    key={book.name}
                    book={book}
                    isActive={currentBook === book.name}
                    currentChapter={currentBook === book.name ? currentChapter : null}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            </div>
            
            {/* New Testament */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                New Testament
              </h3>
              <div className="space-y-1">
                {ntBooks.map(book => (
                  <BookItem 
                    key={book.name}
                    book={book}
                    isActive={currentBook === book.name}
                    currentChapter={currentBook === book.name ? currentChapter : null}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const BookItem = ({ book, isActive, currentChapter, onSelect }) => {
  const [expanded, setExpanded] = useState(isActive);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors flex items-center justify-between",
          isActive ? "bg-indigo-100 text-indigo-900" : "hover:bg-slate-100"
        )}
      >
        <span className="font-medium">{book.name}</span>
        <span className="text-xs text-slate-400">{book.chapters} ch</span>
      </button>
      
      {expanded && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 p-2 bg-slate-50 rounded-lg mt-1">
          {Array.from({ length: book.chapters }, (_, i) => i + 1).map(ch => (
            <button
              key={ch}
              onClick={() => onSelect(book.name, ch)}
              className={cn(
                "w-8 h-8 rounded-md text-xs font-medium transition-colors",
                currentChapter === ch 
                  ? "bg-indigo-600 text-white" 
                  : "bg-white hover:bg-indigo-100 text-slate-600"
              )}
            >
              {ch}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== Verse Component ==============
const Verse = ({ 
  verse, 
  isHighlighted, 
  highlightColor, 
  hasNote,
  isBookmarked,
  onHighlight, 
  onNote, 
  onBookmark,
  fontSize,
  isAuthenticated 
}) => {
  const [showActions, setShowActions] = useState(false);

  const highlightColors = {
    yellow: 'bg-yellow-200',
    green: 'bg-green-200',
    blue: 'bg-blue-200',
    purple: 'bg-purple-200',
    pink: 'bg-pink-200',
  };

  return (
    <div 
      className="group relative py-1"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <span
        id={`verse-${verse.verse}`}
        className={cn(
          "inline leading-relaxed transition-colors rounded px-0.5",
          isHighlighted && highlightColors[highlightColor || 'yellow'],
          !isHighlighted && "hover:bg-slate-100"
        )}
        style={{ fontSize: `${fontSize}px` }}
      >
        <sup className="text-indigo-500 font-semibold mr-1 text-xs">{verse.verse}</sup>
        {verse.text}
        {hasNote && <PenLine className="inline h-3 w-3 text-amber-500 ml-1" />}
        {isBookmarked && <Bookmark className="inline h-3 w-3 text-indigo-500 ml-1 fill-indigo-500" />}
      </span>

      {/* Verse Actions Popover */}
      {showActions && isAuthenticated && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="absolute -right-1 top-0 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="end">
            <div className="flex flex-col gap-1">
              {/* Highlight Colors */}
              <div className="flex gap-1 p-1">
                {Object.keys(highlightColors).map(color => (
                  <button
                    key={color}
                    onClick={() => onHighlight(verse.verse, color)}
                    className={cn(
                      "w-6 h-6 rounded-full transition-transform hover:scale-110",
                      highlightColors[color],
                      isHighlighted && highlightColor === color && "ring-2 ring-offset-1 ring-slate-400"
                    )}
                  />
                ))}
                {isHighlighted && (
                  <button
                    onClick={() => onHighlight(verse.verse, null)}
                    className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center hover:bg-slate-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              
              <div className="border-t pt-1 space-y-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-start h-8"
                  onClick={() => onNote(verse)}
                >
                  <PenLine className="h-3 w-3 mr-2" /> Add Note
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-start h-8"
                  onClick={() => onBookmark(verse.verse)}
                >
                  {isBookmarked ? (
                    <>
                      <BookmarkCheck className="h-3 w-3 mr-2" /> Bookmarked
                    </>
                  ) : (
                    <>
                      <Bookmark className="h-3 w-3 mr-2" /> Bookmark
                    </>
                  )}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-start h-8"
                  onClick={() => {
                    navigator.clipboard.writeText(`${verse.text} (v.${verse.verse})`);
                    toast.success('Verse copied!');
                  }}
                >
                  <Copy className="h-3 w-3 mr-2" /> Copy
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

// ============== Note Dialog ==============
const NoteDialog = ({ open, onClose, verse, book, chapter, existingNote, onSave }) => {
  const [content, setContent] = useState(existingNote || '');

  useEffect(() => {
    setContent(existingNote || '');
  }, [existingNote, verse]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Note for {book} {chapter}:{verse?.verse}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-slate-600 mb-3 italic">"{verse?.text}"</p>
          <Textarea
            placeholder="Write your thoughts..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={5}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(content)}>Save Note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Settings Sheet ==============
const SettingsSheet = ({ fontSize, setFontSize }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon">
          <Settings className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Font Size</label>
            <div className="flex items-center gap-3">
              <Minus className="h-4 w-4 text-slate-400" />
              <Slider
                value={[fontSize]}
                onValueChange={([value]) => setFontSize(value)}
                min={14}
                max={24}
                step={1}
                className="flex-1"
              />
              <Plus className="h-4 w-4 text-slate-400" />
            </div>
            <span className="text-xs text-slate-500 mt-1 block text-center">{fontSize}px</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};


// ============== Bible Version Picker ==============
// Surfaces wldeh's 200+ translations across 140+ languages. Search box +
// flat list — when a language has multiple translations, all show with
// their abbreviation/id, and a "Use as parallel" pin enables side-by-side
// view with the primary version. Both selections persist to the user's
// `bible_user_preferences` row.
const BibleVersionPicker = ({ currentId, parallelId, versionsData, onSelectPrimary, onSelectParallel }) => {
  const [query, setQuery] = useState('');
  const rows = (versionsData?.versions || []);
  const filtered = useMemo(() => {
    if (!query) return rows;
    const s = query.toLowerCase().trim();
    return rows.filter(v =>
      (v.language || '').toLowerCase().includes(s) ||
      (v.name || '').toLowerCase().includes(s) ||
      (v.abbreviation || '').toLowerCase().includes(s) ||
      (v.id || '').toLowerCase().includes(s),
    );
  }, [rows, query]);

  const currentRow = rows.find(v => v.id === currentId);
  const triggerLabel = currentRow
    ? (currentRow.abbreviation || currentRow.id.split('-').pop().toUpperCase())
    : (currentId || 'KJV').toUpperCase();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="bible-version-picker-trigger" className="gap-1 text-xs font-semibold uppercase">
          <Globe className="h-3.5 w-3.5" />
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="border-b p-2">
          <Input
            placeholder={`Search ${versionsData?.total || ''} translations…`}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="h-8"
            data-testid="bible-version-search"
          />
          <p className="text-[10px] text-slate-400 mt-1">{versionsData?.source || 'Public-domain Bible sources'}</p>
        </div>
        <ScrollArea className="h-72">
          <div className="p-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-400 italic p-3 text-center">No translations match "{query}".</p>
            ) : filtered.slice(0, 200).map(v => {
              const isPrimary = v.id === currentId;
              const isParallel = v.id === parallelId;
              return (
                <div key={v.id} className={`flex items-start justify-between gap-1 p-2 rounded hover:bg-slate-50 ${isPrimary ? 'bg-indigo-50' : ''}`}>
                  <button
                    className="flex-1 text-left min-w-0"
                    onClick={() => onSelectPrimary(v.id)}
                    data-testid={`bible-version-${v.id}`}
                  >
                    <div className="text-xs font-medium truncate">{v.name || v.id}</div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1">
                      <span className="truncate">{v.language}</span>
                      <span className="text-slate-300">·</span>
                      <span className="font-mono">{v.id}</span>
                      {isPrimary && <Badge className="ml-1 h-3 px-1 text-[8px] bg-indigo-100 text-indigo-700">Reading</Badge>}
                      {isParallel && <Badge className="ml-1 h-3 px-1 text-[8px] bg-emerald-100 text-emerald-700">Parallel</Badge>}
                    </div>
                  </button>
                  <button
                    className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${isParallel ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-slate-200 text-slate-400 hover:text-emerald-600 hover:border-emerald-300'}`}
                    onClick={() => onSelectParallel(isParallel ? null : v.id)}
                    title={isParallel ? 'Remove parallel view' : 'Add as parallel pane'}
                    data-testid={`bible-version-parallel-${v.id}`}
                    disabled={isPrimary}
                  >
                    {isParallel ? '×' : '∥'}
                  </button>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        {parallelId && (
          <div className="border-t p-2 bg-emerald-50/50">
            <button onClick={() => onSelectParallel(null)} className="text-xs text-emerald-700 hover:underline w-full text-left" data-testid="bible-parallel-clear">
              Turn off parallel view
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};



// ============== Main Component ==============
const EnhancedBibleReader = ({ book: propBook, chapter: propChapter }) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStore();
  const contentRef = useRef(null);

  // Get book/chapter from props or URL params
  const book = propBook || params.book || 'Genesis';
  const chapter = propChapter || parseInt(params.chapter) || 1;

  // Settings state
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('bible_font_size');
    return saved ? parseInt(saved) : 18;
  });

  // Bible version preference — server-stored when logged in, falls back to
  // localStorage for anonymous readers. `parallelBibleId` is the right-hand
  // pane in parallel view; null = parallel off.
  const [bibleId, setBibleId] = useState(() => localStorage.getItem('bible_id') || 'en-kjv');
  const [parallelBibleId, setParallelBibleId] = useState(() => localStorage.getItem('bible_parallel_id') || null);
  const { data: prefs } = useQuery({
    queryKey: ['bible-user-prefs'],
    queryFn: () => api.get('/bible/me/preferences').then(r => r.data),
    enabled: isAuthenticated,
  });
  useEffect(() => {
    if (prefs?.preferred_bible_id) setBibleId(prefs.preferred_bible_id);
    if (prefs?.parallel_bible_id !== undefined) setParallelBibleId(prefs.parallel_bible_id || null);
  }, [prefs]);
  useEffect(() => { localStorage.setItem('bible_id', bibleId); }, [bibleId]);
  useEffect(() => {
    if (parallelBibleId) localStorage.setItem('bible_parallel_id', parallelBibleId);
    else localStorage.removeItem('bible_parallel_id');
  }, [parallelBibleId]);
  const savePref = (patch) => {
    if (!isAuthenticated) return;
    api.put('/bible/me/preferences', patch).catch(() => {});
  };

  
  // Note dialog state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedVerse, setSelectedVerse] = useState(null);

  // Save font size preference
  useEffect(() => {
    localStorage.setItem('bible_font_size', fontSize.toString());
  }, [fontSize]);

  // Save last reading position
  useEffect(() => {
    localStorage.setItem('bible_last_reading', JSON.stringify({ book, chapter }));
  }, [book, chapter]);

  // Scroll to verse if specified
  useEffect(() => {
    const scrollToVerse = location.state?.scrollToVerse;
    if (scrollToVerse && contentRef.current) {
      setTimeout(() => {
        const element = document.getElementById(`verse-${scrollToVerse}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-2', 'ring-indigo-500');
          setTimeout(() => element.classList.remove('ring-2', 'ring-indigo-500'), 2000);
        }
      }, 500);
    }
  }, [location.state]);

  // Fetch chapter data — re-fetches whenever the bible version changes.
  const { data: chapterData, isLoading } = useQuery({
    queryKey: ['bible-chapter', book, chapter, bibleId],
    queryFn: () => api.get(`/bible/${book}/${chapter}?bible_id=${bibleId}`).then(r => r.data),
  });

  // Parallel pane — only fires when the user enables parallel view.
  const { data: parallelData } = useQuery({
    queryKey: ['bible-chapter-parallel', book, chapter, parallelBibleId],
    queryFn: () => api.get(`/bible/${book}/${chapter}?bible_id=${parallelBibleId}`).then(r => r.data),
    enabled: !!parallelBibleId,
  });

  // Bible versions catalog — loaded once per session, used by the picker.
  const { data: versionsData } = useQuery({
    queryKey: ['bible-versions'],
    queryFn: () => api.get('/bible/versions').then(r => r.data),
    staleTime: 60 * 60 * 1000,  // 1h
  });

  // Fetch book info for navigation
  const { data: books = [] } = useQuery({
    queryKey: ['bible-books'],
    queryFn: () => api.get('/bible/books').then(r => r.data),
  });

  // Fetch user's highlights and notes for this chapter
  const { data: highlights = [] } = useQuery({
    queryKey: ['bible-highlights', book],
    queryFn: () => api.get(`/bible/my-highlights?book=${book}`).then(r => r.data),
    enabled: isAuthenticated,
  });

  const { data: notes = [] } = useQuery({
    queryKey: ['bible-notes', book],
    queryFn: () => api.get(`/bible/my-notes?book=${book}`).then(r => r.data),
    enabled: isAuthenticated,
  });

  const { data: bookmarks = [] } = useQuery({
    queryKey: ['bible-bookmarks'],
    queryFn: () => api.get('/bible/my-bookmarks').then(r => r.data),
    enabled: isAuthenticated,
  });

  // Create maps for quick lookup
  const highlightMap = useMemo(() => {
    const map = {};
    highlights.forEach(h => {
      if (h.chapter === chapter) {
        map[h.verse] = h.color;
      }
    });
    return map;
  }, [highlights, chapter]);

  const noteMap = useMemo(() => {
    const map = {};
    notes.forEach(n => {
      if (n.chapter === chapter) {
        map[n.verse] = n.content;
      }
    });
    return map;
  }, [notes, chapter]);

  const bookmarkSet = useMemo(() => {
    return new Set(
      bookmarks
        .filter(b => b.book === book && b.chapter === chapter)
        .map(b => b.verse)
    );
  }, [bookmarks, book, chapter]);

  // Mutations
  const highlightMutation = useMutation({
    mutationFn: ({ verse, color }) => {
      if (color) {
        return api.post('/bible/highlights', { book, chapter, verse, color });
      } else {
        return api.delete(`/bible/highlights/${book}/${chapter}/${verse}`);
      }
    },
    onSuccess: () => queryClient.invalidateQueries(['bible-highlights', book]),
  });

  const noteMutation = useMutation({
    mutationFn: (content) => api.post('/bible/notes', { 
      book, chapter, verse: selectedVerse?.verse, content 
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['bible-notes', book]);
      toast.success('Note saved!');
      setNoteDialogOpen(false);
    },
  });

  const bookmarkMutation = useMutation({
    mutationFn: (verse) => api.post('/bible/bookmarks', { book, chapter, verse }),
    onSuccess: () => {
      queryClient.invalidateQueries(['bible-bookmarks']);
      toast.success('Bookmark added!');
    },
  });

  // Navigation helpers
  const currentBookInfo = books.find(b => b.name === book);
  const bookIndex = books.findIndex(b => b.name === book);
  
  const canGoPrev = chapter > 1 || bookIndex > 0;
  const canGoNext = chapter < (currentBookInfo?.chapters || 1) || bookIndex < books.length - 1;

  const goToPrev = () => {
    if (chapter > 1) {
      navigate(`/bible/${book}/${chapter - 1}`);
    } else if (bookIndex > 0) {
      const prevBook = books[bookIndex - 1];
      navigate(`/bible/${prevBook.name}/${prevBook.chapters}`);
    }
  };

  const goToNext = () => {
    if (chapter < (currentBookInfo?.chapters || 1)) {
      navigate(`/bible/${book}/${chapter + 1}`);
    } else if (bookIndex < books.length - 1) {
      const nextBook = books[bookIndex + 1];
      navigate(`/bible/${nextBook.name}/1`);
    }
  };

  const handleSelectBookChapter = (newBook, newChapter) => {
    navigate(`/bible/${newBook}/${newChapter}`);
  };

  const handleHighlight = (verse, color) => {
    if (!isAuthenticated) {
      toast.info('Sign in to highlight verses');
      return;
    }
    highlightMutation.mutate({ verse, color });
  };

  const handleNote = (verse) => {
    if (!isAuthenticated) {
      toast.info('Sign in to add notes');
      return;
    }
    setSelectedVerse(verse);
    setNoteDialogOpen(true);
  };

  const handleBookmark = (verse) => {
    if (!isAuthenticated) {
      toast.info('Sign in to bookmark verses');
      return;
    }
    bookmarkMutation.mutate(verse);
  };

  const markChapterRead = () => {
    if (!isAuthenticated) {
      toast.info('Sign in to track reading progress');
      return;
    }
    // Would need an API endpoint for this
    toast.success('Chapter marked as read!');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const verses = chapterData?.verses || [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookNavigationSheet 
            currentBook={book}
            currentChapter={chapter}
            onSelect={handleSelectBookChapter}
          />
        </div>

        <h1 className="font-bold text-lg text-slate-900">
          {book} {chapter}
        </h1>

        <div className="flex items-center gap-1">
          <BibleVersionPicker
            currentId={bibleId}
            parallelId={parallelBibleId}
            versionsData={versionsData}
            onSelectPrimary={(id) => { setBibleId(id); savePref({ preferred_bible_id: id }); }}
            onSelectParallel={(id) => { setParallelBibleId(id); savePref({ parallel_bible_id: id }); }}
          />
          <SettingsSheet fontSize={fontSize} setFontSize={setFontSize} />
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 px-4 py-6" ref={contentRef}>
        <div className={`mx-auto ${parallelBibleId ? 'max-w-5xl' : 'max-w-2xl'}`}>
          {verses.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-600 mb-2">Chapter Not Available</h3>
                <p className="text-sm text-slate-500">
                  This chapter hasn't been imported yet. Please check back later.
                </p>
              </CardContent>
            </Card>
          ) : parallelBibleId ? (
            // Parallel view — two synchronised verse columns aligned by verse number.
            <div className="grid grid-cols-2 gap-6 text-slate-800" data-testid="bible-parallel-view">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2" data-testid="parallel-label-primary">{bibleId.toUpperCase()}</p>
                <div className="space-y-1">
                  {verses.map((verse) => (
                    <Verse
                      key={`p1-${verse.verse}`}
                      verse={verse}
                      isHighlighted={!!highlightMap[verse.verse]}
                      highlightColor={highlightMap[verse.verse]}
                      hasNote={!!noteMap[verse.verse]}
                      isBookmarked={bookmarkSet.has(verse.verse)}
                      onHighlight={handleHighlight}
                      onNote={handleNote}
                      onBookmark={handleBookmark}
                      fontSize={fontSize}
                      isAuthenticated={isAuthenticated}
                    />
                  ))}
                </div>
              </div>
              <div className="border-l pl-6">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2" data-testid="parallel-label-secondary">{parallelBibleId.toUpperCase()}</p>
                <div className="space-y-1">
                  {(parallelData?.verses || []).map((verse) => (
                    <p key={`p2-${verse.verse}`} style={{ fontSize: `${fontSize}px`, lineHeight: 1.7 }} className="text-slate-700">
                      <span className="text-xs font-bold text-indigo-500 mr-1">{verse.verse}</span>{verse.text}
                    </p>
                  ))}
                  {(!parallelData || parallelData?.verses?.length === 0) && (
                    <p className="text-xs text-slate-400 italic">Translation not available for this chapter.</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-1 text-slate-800">
              {verses.map((verse) => (
                <Verse
                  key={verse.verse}
                  verse={verse}
                  isHighlighted={!!highlightMap[verse.verse]}
                  highlightColor={highlightMap[verse.verse]}
                  hasNote={!!noteMap[verse.verse]}
                  isBookmarked={bookmarkSet.has(verse.verse)}
                  onHighlight={handleHighlight}
                  onNote={handleNote}
                  onBookmark={handleBookmark}
                  fontSize={fontSize}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer Navigation */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t px-4 py-3 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPrev}
          disabled={!canGoPrev}
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={markChapterRead}
          className="gap-2"
        >
          <CheckCircle className="h-4 w-4" /> Mark Complete
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={goToNext}
          disabled={!canGoNext}
        >
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Note Dialog */}
      <NoteDialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        verse={selectedVerse}
        book={book}
        chapter={chapter}
        existingNote={noteMap[selectedVerse?.verse]}
        onSave={(content) => noteMutation.mutate(content)}
      />
    </div>
  );
};

export default EnhancedBibleReader;
